import { oneLine } from "shared/imports/stringFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

// taken from app/texting/constants.py
export const MAX_CHARS_PER_TEXT = 160

// percentage of quota an organization has to reach before showing the
// warning banner on the texting resource table
export const TEXT_LIMIT_PERCENTAGE_THRESHOLD = 75

// Identify placeholders as strings containing "{{" and "}}" with no spaces between
// This regex does not identify if a placeholder is valid, only that matching text
// contains a syntactically valid placeholder
export const PLACEHOLDER_REGEX = /{{([^ ]*)}}/gm

// This regex is borrowed from the backend, app/texting/constants.py
export const LINK_REGEX =
    /\b(?:https?:\/\/)?(?:www\.)?(?:[^.\s]+\.)?(?:[^.@\s]+\.)(?:com|us|net|gov|org|mil|edu|app|io|wiki|to)([^.\s]|(\.\S))*/

// This regex checks for the presence of DEFAULT_SUBSCRIPTION_DISCLAIMER in a string
export const DISCLAIMER_REGEX =
    /[\s\S]*Reply START to receive texts\. Reply STOP to stop, HELP for info\. Msg&data rates may apply\./

// This constant is borrowed from the backend, app/texting/constants.py
// If this needs to be changed, change the above regex as well as this string in the backend
export const DEFAULT_SUBSCRIPTION_DISCLAIMER =
    "Reply START to receive texts. Reply STOP to stop, HELP for info. Msg&data rates may apply."

export const descriptionSectionDescription = `\
    Choose a description for this text. This will be what you use to\
    identify it and will not be visible anywhere except within Quorum.
`

export const textContainsLinksWarning =
    "Your message contains links; we will automatically replace all links with short links which enable us to track engagement."
export const textContainsPlaceholdersWarning =
    "Your message contains placeholders, so the exact number of messages sent may vary per recipient."

export const noPreviewsMessage = "No previews; have you added recipients that are opted-in to receiving texts?"

export const getQuotaMessage = ({ limit, percentage }) => oneLine`
    Heads up: You've been sending a lot of texts—way to go!
    This is a friendly reminder that your organization has a 30-day rolling sending limit of ${limit}
    messages and that you have used ${percentage}% of your quota.
    If you are interested in increasing your messaging limit,
    reach out to your Customer Success Manager, or support@quorum.us.
`

export const noPhoneNumberMessage = `\
    It looks like your organization does not have a phone number to send messages\
    from. You won't be able to send any messages. Please contact us to resolve this.
    `

export const formErrorTypes = {
    placeholderValidation: {
        value: "invalid_placeholder",
        title: "Invalid Placeholders",
    },
}

export const swalConfigs = {
    invalidTestNumberFormat: {
        icon: "warning",
        title: "Phone Number Format",
        text: "You must enter a valid phone number. Valid phone numbers begin with a country code ('+1') and contain an area code.",
    },
    messageLimitReached: {
        icon: "error",
        title: "Message Limit Reached",
        text: `This message cannot be sent because your organization exceeds its sending\
            limit. If you are interested in increasing your monthly messaging limit, reach out\
            to your Customer Success Manager, or support@quorum.us`,
    },
    noPhoneNumberInput: {
        icon: "warning",
        title: "Missing Phone Number",
        text: "You must enter a valid phone number.",
    },
    noRecipients: {
        icon: "warning",
        title: "No Recipients",
        text: "It looks like you don't have any recipients selected. Please add some contacts and try again.",
    },
    noText: {
        icon: "warning",
        title: "Empty Message",
        text: "It looks like your message is empty. Please add some content to your message and try again.",
    },
    somethingWentWrong: {
        icon: "warning",
        title: "Oops!",
        text: "Looks like something went wrong. Please try again or refresh this page.",
    },
    tooManyTestNumbers: {
        icon: "warning",
        title: "Too many numbers",
        text: "You may only send test messages to one number at a time. Please remove extra numbers.",
    },
}

export const defaultSearchifySavedSearchSelectionObj = {
    added_ids: [],
    deleted_ids: [],
    filters: [],
    data_type: DjangIO.app.models.QuorumDataType.supporter.value,
    user: DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(Userdata.id),
    estimated_count: 0,
}

// This Searchify object does not match the specification
export const defaultSearchifySelectionObj = {
    added_ids: [],
    deleted_ids: [],
    filters: [],
    data_type: DjangIO.app.models.QuorumDataType.supporter.value,
    user: DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(Userdata.id),
    estimated_count: 0,
}

export const defaultSafedSearchObj = {
    added_ids: [],
    deleted_ids: [],
    filters: [],
    estimated_count: 0,
}
export const defaultSearchifyObj = {
    addedIds: [],
    deletedIds: [],
    querysetList: [],
    searchifyCount: 0,
}

const TextDeliveryStatus = DjangIO.app.texting.types.TextDeliveryStatus

const tableDisplayOptions = {
    sent: {
        value: 0,
        label: "Sent",
        filter: {
            current_status__in: TextDeliveryStatus.items()
                .filter(
                    (status) =>
                        status.value !== TextDeliveryStatus.draft.value &&
                        status.value !== TextDeliveryStatus.to_be_processed.value,
                )
                .map((status) => status.value),
            dehydrate_extra: ["creator", "project_names"],
        },
        url: "sent",
        icon: "paper-plane",
        iconFamily: "far",
    },
    draft: {
        value: 1,
        label: "Draft",
        filter: {
            current_status: TextDeliveryStatus.draft.value,
            dehydrate_extra: ["creator", "project_names"],
        },
        url: "draft",
        icon: "edit",
    },
    scheduled: {
        value: 2,
        label: "Scheduled",
        filter: {
            current_status: TextDeliveryStatus.to_be_processed.value,
            dehydrate_extra: ["creator", "project_names"],
        },
        url: "scheduled",
    },
}

export { tableDisplayOptions }
